"use client";

import React from "react";
import { BookOpen, Compass, FileText, Layers } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const topicPills = [{
  label: "Executive Strategy",
  id: "strategy"
}, {
  label: "Revenue Operations",
  id: "revops"
}, {
  label: "Enterprise AI & Tech",
  id: "tech"
}, {
  label: "B2B Marketing & Growth",
  id: "marketing"
}];
const pillars = [{
  icon: FileText,
  title: "Field-tested playbooks",
  description: "Step-by-step frameworks created from real enterprise deployments, not theoretical scenarios."
}, {
  icon: Layers,
  title: "Complete working templates",
  description: "Ready-to-use worksheets, evaluation matrices, and organizational blueprints included with every guide."
}, {
  icon: Compass,
  title: "No vendor sponsorships",
  description: "Impartial analysis and vendor-neutral recommendations maintained under strict editorial independence."
}];
export default function Intro() {
  const shouldReduceMotion = useReducedMotion();
  const animationProps = shouldReduceMotion ? {
    initial: {
      opacity: 1,
      y: 0
    },
    animate: {
      opacity: 1,
      y: 0
    }
  } : {
    initial: {
      opacity: 0,
      y: 12
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.4,
      ease: [0.16, 1, 0.3, 1]
    }
  };
  return <section id="intro" data-nav="light" className="relative bg-background text-foreground py-16 sm:py-20 lg:py-24 border-b border-border">
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12">
        {/* Editorial Masthead Rule */}
        <div className="mb-10 sm:mb-14">
          <div className="h-[1px] bg-foreground/20 w-full mb-[2px]" />
          <div className="h-[0.5px] bg-border w-full flex items-center justify-between pt-3">
            <span className="text-[11px] uppercase tracking-wider font-mono text-muted-foreground">
              MinfoMedia Knowledge Series
            </span>
            <span className="text-[11px] font-mono text-muted-foreground">
              Vol. 2025 • Reference Guides &amp; Tactical Blueprints
            </span>
          </div>
        </div>

        {/* Centered Hero Intro Content */}
        <motion.div {...animationProps} className="max-w-[860px] mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-sm border border-border bg-card text-muted-foreground text-xs font-mono tracking-normal mb-6">
            <BookOpen className="size-3.5 text-foreground" />
            <span>Executive Ebooks &amp; Strategic Playbooks</span>
          </div>

          <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-[3.25rem] font-semibold leading-[1.18] tracking-tight text-foreground mb-6">
            Practical playbooks for modern enterprise teams
          </h1>

          <p className="text-base sm:text-lg md:text-xl text-muted-foreground font-sans leading-relaxed mb-8 max-w-[720px] mx-auto">
            Tactical frameworks, execution playbooks, and strategic guides designed to help senior leaders build resilient marketing, technology, and operations programs.
          </p>

          {/* Quick topic tags */}
          <div className="flex flex-wrap items-center justify-center gap-2 mb-10">
            <span className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mr-1">
              Browse by domain:
            </span>
            {topicPills.map((pill, i) => <span key={pill.id} data-index={i} className="inline-flex items-center px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-xs font-semibold tracking-wider hover:bg-secondary/80 transition-colors">
                {pill.label}
              </span>)}
          </div>

          <div className="flex flex-wrap items-center justify-center gap-4">
            <Button asChild className="rounded-sm px-6 py-3.5 h-auto text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90">
              <Link to="/ResourcesPage">
                Explore full library
              </Link>
            </Button>
            <Button asChild variant="outline" className="rounded-sm px-6 py-3.5 h-auto text-sm font-medium border-primary text-primary hover:bg-primary/5">
              <Link to="/ResearchReportsPage">
                View research reports
              </Link>
            </Button>
          </div>
        </motion.div>

        {/* Editorial Pillar Summary */}
        <div className="mt-16 sm:mt-20 pt-12 border-t border-border grid grid-cols-1 md:grid-cols-3 gap-8 sm:gap-10">
          {pillars.map((pillar, i) => {
          const IconComponent = pillar.icon;
          return <div key={pillar.title} data-index={i} className="flex flex-col items-start text-left">
                <div className="p-2.5 rounded-sm bg-secondary text-foreground mb-4 border border-border">
                  <IconComponent className="size-5" />
                </div>
                <h2 className="font-serif text-lg font-semibold text-foreground mb-2">
                  {pillar.title}
                </h2>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {pillar.description}
                </p>
              </div>;
        })}
        </div>
      </div>
    </section>;
}